import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Row, Col, Table, Button, Badge, Form, Spinner, ButtonGroup } from 'react-bootstrap';
import { Plus, Copy, Printer, CheckCircle, Edit, FileText } from 'lucide-react';
import { getQuotes, duplicateQuote, convertQuoteToTrips } from '../api';
import QuoteEditorModal from './QuoteEditorModal';
import { generateQuotePDFPrintWindow } from './QuotePDFGenerator';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

const QuotesView = () => {
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    
    const [showEditorModal, setShowEditorModal] = useState(false);
    const [selectedQuote, setSelectedQuote] = useState(null);

    const loadQuotes = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (searchTerm.trim()) params.search = searchTerm.trim();
            const data = await getQuotes(params);
            setQuotes(data || []);
        } catch (e) {
            console.error("Error al cargar cotizaciones:", e);
            toast.error("Error al cargar las cotizaciones.");
        } finally {
            setLoading(false);
        }
    }, [statusFilter, searchTerm]);

    useEffect(() => {
        loadQuotes();
    }, [loadQuotes]);

    const handleNewQuote = () => {
        setSelectedQuote(null);
        setShowEditorModal(true);
    };

    const handleEditQuote = (quote) => {
        setSelectedQuote(quote);
        setShowEditorModal(true);
    };

    const handleDuplicateQuote = async (quote) => {
        try {
            const dup = await duplicateQuote(quote.id);
            toast.success(`📋 Cotización duplicada con éxito como #${dup.quote_number}`);
            loadQuotes();
        } catch (e) {
            console.error(e);
            toast.error("Error al duplicar cotización.");
        }
    };

    const handleConvertToTrips = async (quote) => {
        if (!window.confirm(`¿Estás seguro de convertir la Cotización #${quote.quote_number} en viajes reales en el Despacho Diario?`)) {
            return;
        }
        try {
            const res = await convertQuoteToTrips(quote.id);
            toast.success(`🎉 ${res.message}`);
            loadQuotes();
        } catch (e) {
            console.error(e);
            toast.error(`⚠️ Error al convertir: ${e.response?.data?.detail || e.message}`);
        }
    };

    // KPIs
    const totalQuotesCount = quotes.length;
    const totalSalesAmount = quotes.reduce((sum, q) => sum + (q.total_price || 0), 0);
    const approvedQuotesCount = quotes.filter(q => q.status === 'APPROVED').length;
    const avgProfitMargin = quotes.length > 0
        ? (quotes.reduce((sum, q) => sum + (q.total_price > 0 ? ((q.profit_amount / q.total_price) * 100) : 0), 0) / quotes.length).toFixed(1)
        : '0.0';

    return (
        <Container fluid className="p-4 space-y-4">
            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div>
                    <h2 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                        <FileText className="text-primary" size={28} /> Cotizador de Eventos y Servicios Especiales
                    </h2>
                    <p className="text-muted small mb-0">
                        Genera cotizaciones profesionales con margen automático (30%+), guarda el historial histórico para duplicar en 1 clic y convierte cotizaciones aprobadas en viajes reales.
                    </p>
                </div>
                <Button variant="primary" className="fw-bold shadow-sm" onClick={handleNewQuote}>
                    <Plus size={18} className="me-1" /> Nueva Cotización
                </Button>
            </div>

            {/* TARJETAS KPI */}
            <Row className="g-3 mb-4">
                <Col md={3} sm={6}>
                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-primary border-4">
                        <div className="text-muted small fw-bold mb-1 text-uppercase">TOTAL COTIZACIONES</div>
                        <h3 className="fw-bold mb-0 text-primary">{totalQuotesCount}</h3>
                        <small className="text-muted">Cotizaciones registradas</small>
                    </Card>
                </Col>
                <Col md={3} sm={6}>
                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-success border-4">
                        <div className="text-muted small fw-bold mb-1 text-uppercase">MONTO COTIZADO</div>
                        <h3 className="fw-bold mb-0 text-success">
                            ${Math.round(totalSalesAmount).toLocaleString('es-CL')}
                        </h3>
                        <small className="text-muted">Suma total de propuestas</small>
                    </Card>
                </Col>
                <Col md={3} sm={6}>
                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-info border-4">
                        <div className="text-muted small fw-bold mb-1 text-uppercase">COTIZACIONES APROBADAS</div>
                        <h3 className="fw-bold mb-0 text-info">{approvedQuotesCount}</h3>
                        <small className="text-muted">Convertidas a reservas</small>
                    </Card>
                </Col>
                <Col md={3} sm={6}>
                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-warning border-4">
                        <div className="text-muted small fw-bold mb-1 text-uppercase">MARGEN PROMEDIO</div>
                        <h3 className="fw-bold mb-0 text-warning">{avgProfitMargin}%</h3>
                        <small className="text-muted">Meta mínima: 30%</small>
                    </Card>
                </Col>
            </Row>

            {/* BARRA DE FILTROS Y BÚSQUEDA */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-3 bg-light rounded">
                    <Row className="align-items-center g-2">
                        <Col md={5}>
                            <Form.Control
                                type="text"
                                placeholder="🔍 Buscar por cliente, N° cotización, ruta o notas..."
                                size="sm"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Select
                                size="sm"
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                            >
                                <option value="">📋 Todos los Estados</option>
                                <option value="DRAFT">📝 Borrador</option>
                                <option value="SENT">📤 Enviada al Cliente</option>
                                <option value="APPROVED">✅ Aprobada / Convertida</option>
                                <option value="REJECTED">❌ Rechazada</option>
                            </Form.Select>
                        </Col>
                        <Col md={4} className="text-end">
                            <Button variant="outline-primary" size="sm" onClick={loadQuotes} disabled={loading} className="fw-bold me-2">
                                {loading ? <Spinner size="sm" animation="border" /> : '🔄 Refrescar'}
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* TABLA DE COTIZACIONES */}
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <Table responsive hover className="align-middle mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th className="ps-3">N° Cotización</th>
                                <th>Fecha Emisión</th>
                                <th>Cliente / Empresa</th>
                                <th>Itinerario / Tramos</th>
                                <th>Moneda</th>
                                <th className="text-end">Monto Venta</th>
                                <th className="text-center">Margen %</th>
                                <th>Estado</th>
                                <th className="text-end pe-3">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="9" className="text-center py-5">
                                        <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                                        Cargando cotizaciones...
                                    </td>
                                </tr>
                            ) : quotes.length === 0 ? (
                                <tr>
                                    <td colSpan="9" className="text-center py-5 text-muted">
                                        No se encontraron cotizaciones registradas. ¡Haz clic en "Nueva Cotización" para crear la primera!
                                    </td>
                                </tr>
                            ) : (
                                quotes.map(quote => {
                                    const isUSD = quote.currency === 'USD';
                                    const formattedPrice = isUSD 
                                        ? quote.total_price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                        : Math.round(quote.total_price).toLocaleString('es-CL');
                                    const marginPct = quote.total_price > 0 
                                        ? ((quote.profit_amount / quote.total_price) * 100).toFixed(1) 
                                        : '0.0';

                                    return (
                                        <tr key={quote.id}>
                                            <td className="ps-3 fw-bold text-primary">
                                                #{quote.quote_number}
                                            </td>
                                            <td className="small text-muted">
                                                {quote.created_at ? format(new Date(quote.created_at), 'dd/MM/yyyy') : '-'}
                                            </td>
                                            <td>
                                                <div className="fw-bold text-dark">{quote.client_name}</div>
                                                {quote.client_contact && <div className="small text-muted">Atn: {quote.client_contact}</div>}
                                            </td>
                                            <td>
                                                <div className="small">
                                                    <strong>{quote.items ? quote.items.length : 0} tramo(s)</strong>
                                                    {quote.items && quote.items.length > 0 && (
                                                        <div className="text-muted text-truncate" style={{ maxWidth: '250px' }}>
                                                            {quote.items[0].origin} ➔ {quote.items[0].destination}
                                                            {quote.items.length > 1 && ` (+${quote.items.length - 1} más)`}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <Badge bg={isUSD ? 'success' : 'light'} text={isUSD ? 'white' : 'dark'} className="border fw-bold">
                                                    {quote.currency || 'CLP'}
                                                </Badge>
                                            </td>
                                            <td className="text-end fw-bold text-dark fs-6">
                                                {isUSD ? `USD $${formattedPrice}` : `$${formattedPrice}`}
                                            </td>
                                            <td className="text-center">
                                                <Badge 
                                                    bg={parseFloat(marginPct) >= 30 ? 'success' : 'warning'} 
                                                    className="px-2 py-1 shadow-sm"
                                                >
                                                    {marginPct}%
                                                </Badge>
                                            </td>
                                            <td>
                                                <Badge 
                                                    bg={
                                                        quote.status === 'APPROVED' ? 'success' : 
                                                        quote.status === 'SENT' ? 'info' : 
                                                        quote.status === 'REJECTED' ? 'danger' : 'secondary'
                                                    }
                                                >
                                                    {quote.status === 'APPROVED' ? '✅ Aprobada' : 
                                                     quote.status === 'SENT' ? '📤 Enviada' : 
                                                     quote.status === 'REJECTED' ? '❌ Rechazada' : '📝 Borrador'}
                                                </Badge>
                                            </td>
                                            <td className="text-end pe-3">
                                                <ButtonGroup size="sm">
                                                    <Button 
                                                        variant="outline-primary" 
                                                        onClick={() => handleEditQuote(quote)}
                                                        title="Editar cotización"
                                                    >
                                                        <Edit size={14} />
                                                    </Button>
                                                    <Button 
                                                        variant="outline-dark" 
                                                        onClick={() => handleDuplicateQuote(quote)}
                                                        title="Duplicar cotización (Reutilizar)"
                                                    >
                                                        <Copy size={14} />
                                                    </Button>
                                                    <Button 
                                                        variant="outline-secondary" 
                                                        onClick={() => generateQuotePDFPrintWindow(quote)}
                                                        title="Imprimir / PDF"
                                                    >
                                                        <Printer size={14} />
                                                    </Button>
                                                    {quote.status !== 'APPROVED' && (
                                                        <Button 
                                                            variant="outline-success" 
                                                            onClick={() => handleConvertToTrips(quote)}
                                                            title="Convertir a Reservas en Despacho Diario"
                                                        >
                                                            <CheckCircle size={14} />
                                                        </Button>
                                                    )}
                                                </ButtonGroup>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* MODAL EDITOR DE COTIZACIÓN */}
            <QuoteEditorModal
                show={showEditorModal}
                onHide={() => setShowEditorModal(false)}
                quote={selectedQuote}
                onSaved={loadQuotes}
            />
        </Container>
    );
};

export default QuotesView;
